<template>
  <div class="page-container">
    <div class="page-header">
      <div class="page-title-wrap">
        <h1>Avances Salariales</h1>
        <p class="page-subtitle">Suivi des avances par employé</p>
      </div>
    </div>

    <!-- Search Staff -->
    <div class="search-section">
      <div class="search-card">
        <div class="search-wrap">
          <svg class="search-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input v-model="searchQuery" type="text" placeholder="Rechercher un employé..." @input="searchUsers"
            class="search-input" />
        </div>

        <!-- Search Results -->
        <div v-if="users && users.length" class="search-results">
          <div v-for="(staff, index) in users" :key="index" class="search-result-item" @click="fetchAvances(staff)">
            <img :src="staffImage(staff)" class="result-avatar" />
            <div>
              <div class="result-name">{{ staff.First_name }} {{ staff.Last_name }}</div>
              <div class="result-post">{{ staff.Post_name }}</div>
            </div>
            <svg class="result-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </div>
        <div v-if="searchError" class="search-error">{{ searchError }}</div>
      </div>
    </div>

    <!-- Selected Staff -->
    <div v-if="selectedStaff" class="staff-banner">
      <button class="back-list-btn" type="button" @click="returnToList" aria-label="Retour à la liste">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
        <span>Liste</span>
      </button>
      <img :src="staffImage(selectedStaff)" class="banner-avatar" />
      <div class="banner-info">
        <h2>{{ selectedStaff.First_name }} {{ selectedStaff.Last_name }}</h2>
        <span class="staff-post-badge">{{ selectedStaff.Post_name }}</span>
      </div>
      <div class="month-select-wrap">
        <label class="form-label">Mois</label>
        <MonthPickerButton v-model="selectedMonth" @change="fetchAvances(selectedStaff)" />
      </div>
      <div v-if="selectedStaff.DaysWorked" class="days-info">
        <span class="days-badge">{{ selectedStaff.DaysWorked }} j travaillés</span>
      </div>
    </div>

    <!-- Avances & Primes Section -->
    <div v-if="selectedStaff" class="avance-section">
      <!-- Error Notice if exists -->
      <div v-if="fetchError" class="info-notice">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        {{ fetchError }}
      </div>
      <!-- Salary Summary -->
      <div class="salary-summary">
        <div class="summary-card summary-blue">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          </div>
          <div>
            <div class="summary-value">{{ staffSalary.toFixed(2) }} DH</div>
            <div class="summary-label">Salaire mensuel</div>
          </div>
        </div>
        <div class="summary-card summary-teal">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>
          <div>
            <div class="summary-value">{{ monthEarnedSalary.toFixed(2) }} DH</div>
            <div class="summary-label">Salaire jours ({{ monthWorkedDays }} j)</div>
          </div>
        </div>
        <div class="summary-card summary-amber">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <div class="summary-value">{{ totalAvance.toFixed(2) }} DH</div>
            <div class="summary-label">Total avances</div>
          </div>
        </div>
        <div class="summary-card summary-orange">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div>
            <div class="summary-value">{{ totalPrimesAmount.toFixed(2) }} DH</div>
            <div class="summary-label">Total primes</div>
          </div>
        </div>
        <div class="summary-card summary-green">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div>
            <div class="summary-value">{{ formatDirham(settlementRemainingAmount) }} DH</div>
            <div class="summary-label">Reste à régler</div>
          </div>
        </div>
        <div class="summary-card summary-blue">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div class="summary-value">{{ formatDirham(totalSettlementsAmount) }} DH</div>
            <div class="summary-label">Déjà réglé</div>
          </div>
        </div>
        <div v-if="selectedStaffMassData" class="summary-card summary-teal">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <div>
            <div class="summary-value status-value" :class="salaryStatusClass">{{ salaryStatusText }}</div>
            <div class="summary-label">Statut salaire</div>
          </div>
        </div>
        <div v-if="advanceToRecoverAmount > 0" class="summary-card summary-red">
          <div class="summary-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </div>
          <div>
            <div class="summary-value value-negative">{{ advanceRecoveryRemainingAmount.toFixed(2) }} DH</div>
            <div class="summary-label">Avance à récupérer</div>
            <div v-if="advanceRecoveredAmount > 0" class="summary-note">Récupéré: {{ advanceRecoveredAmount.toFixed(2) }} DH</div>
          </div>
        </div>
      </div>

      <div v-if="selectedStaffMassData" class="salary-flow-panel">
        <div class="flow-section">
          <div class="flow-header">
            <span>Mois sélectionné</span>
            <strong>{{ selectedMonth || '-' }}</strong>
          </div>
          <div class="flow-grid">
            <div class="flow-item">
              <span>Jours travaillés</span>
              <strong>{{ selectedStaffMassData?.days_worked || 0 }} j</strong>
            </div>
            <div class="flow-item">
              <span>Salaire généré</span>
              <strong>{{ monthEarnedSalary.toFixed(2) }} DH</strong>
            </div>
            <div class="flow-item">
              <span>Avances du mois</span>
              <strong>{{ totalAvance.toFixed(2) }} DH</strong>
            </div>
            <div class="flow-item">
              <span>Net du mois</span>
              <strong :class="{ 'value-negative': monthNetAmount < 0 }">{{ monthNetAmount.toFixed(2) }} DH</strong>
            </div>
          </div>
        </div>

        <div class="flow-section previous-flow">
          <div class="flow-header">
            <span>Anciens mois</span>
            <strong>{{ carryOverAmount.toFixed(2) }} DH</strong>
          </div>
          <div class="flow-grid">
            <div class="flow-item">
              <span>Dette précédente</span>
              <strong>{{ carryOverAmount.toFixed(2) }} DH</strong>
            </div>
            <div class="flow-item">
              <span>Récupéré ce mois</span>
              <strong>{{ advanceRecoveredAmount.toFixed(2) }} DH</strong>
            </div>
            <div class="flow-item">
              <span>Reste ancienne dette</span>
              <strong>{{ advanceRecoveryRemainingAmount.toFixed(2) }} DH</strong>
            </div>
            <div class="flow-item final-flow">
              <span>À régler maintenant</span>
              <strong>{{ formatDirham(settlementRemainingAmount) }} DH</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Add New Avance Form -->
      <div class="form-card add-avance-form action-accordion" :class="{ open: isActionOpen('avance') }">
        <button class="form-header accordion-header" type="button" @click="toggleActionPanel('avance')">
          <h3>Ajouter une avance</h3>
          <span>{{ isActionOpen('avance') ? 'Fermer' : 'Ouvrir' }}</span>
        </button>
        <div v-if="isActionOpen('avance')" class="form-group-row">
          <div class="form-group">
            <label class="form-label">Date et heure</label>
            <input type="datetime-local" v-model="currentDateTime" class="form-input" @input="markDateTimeManual" />
          </div>
          <div class="form-group">
            <label class="form-label">Montant (DH)</label>
            <input type="number" step="0.01" v-model.number="newAvanceAmount" placeholder="0.00" class="form-input" />
          </div>
          <div class="form-group form-group-action">
            <button @click="addNewAvance" class="btn btn-success" :disabled="!newAvanceAmount">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Ajouter
            </button>
          </div>
        </div>
      </div>

      <div class="form-card add-avance-form action-accordion" :class="{ open: isActionOpen('prime') }">
        <button class="form-header accordion-header" type="button" @click="toggleActionPanel('prime')">
          <h3>Ajouter une prime</h3>
          <span>{{ isActionOpen('prime') ? 'Fermer' : 'Ouvrir' }}</span>
        </button>
        <div v-if="isActionOpen('prime')" class="form-group-row">
          <div class="form-group">
            <label class="form-label">Date et heure</label>
            <input type="datetime-local" v-model="currentDateTime" class="form-input" @input="markDateTimeManual" />
          </div>
          <div class="form-group">
            <label class="form-label">Montant (DH)</label>
            <input type="number" step="0.01" v-model.number="newPrimeAmount" placeholder="0.00" class="form-input" />
          </div>
          <div class="form-group form-group-action">
            <button @click="addPrime" class="btn btn-secondary" :disabled="!newPrimeAmount">
              Ajouter
            </button>
          </div>
        </div>
      </div>

      <div class="form-card add-avance-form action-accordion" :class="{ open: isActionOpen('settlement') }">
        <button class="form-header accordion-header" type="button" @click="toggleActionPanel('settlement')">
          <h3>Règlement salaire</h3>
          <span>{{ isActionOpen('settlement') ? 'Fermer' : 'Ouvrir' }}</span>
        </button>
        <div v-if="isActionOpen('settlement')" class="form-group-row">
          <div class="form-group">
            <label class="form-label">Date et heure</label>
            <input type="datetime-local" v-model="currentDateTime" class="form-input" @input="markDateTimeManual" />
          </div>
          <div class="form-group">
            <label class="form-label">Montant (DH)</label>
            <input type="number" step="1" :max="settlementRemainingRounded" v-model.number="newSettlementAmount" class="form-input" />
          </div>
          <div class="form-group">
            <label class="form-label">Note</label>
            <input type="text" v-model="newSettlementNote" class="form-input" placeholder="Optionnel" />
          </div>
          <div class="form-group form-group-action">
            <button @click="addSalarySettlement" class="btn btn-primary" :disabled="!newSettlementAmount || settlementRemainingRounded <= 0 || settlementAmountRounded > settlementRemainingRounded">
              Régler {{ selectedMonth }}
            </button>
          </div>
        </div>
      </div>

      <div class="form-card add-avance-form action-accordion" :class="{ open: isActionOpen('retenue') }">
        <button class="form-header accordion-header" type="button" @click="toggleActionPanel('retenue')">
          <h3>Ajouter une retenue</h3>
          <span>{{ isActionOpen('retenue') ? 'Fermer' : 'Ouvrir' }}</span>
        </button>
        <div v-if="isActionOpen('retenue')" class="form-group-row">
          <div class="form-group">
            <label class="form-label">Date et heure</label>
            <input type="datetime-local" v-model="currentDateTime" class="form-input" @input="markDateTimeManual" />
          </div>
          <div class="form-group">
            <label class="form-label">Montant (DH)</label>
            <input type="number" step="0.01" v-model.number="newRetenueAmount" class="form-input" />
          </div>
          <div class="form-group">
            <label class="form-label">Motif</label>
            <input type="text" v-model="newRetenueReason" class="form-input" placeholder="Erreur caisse, casse..." />
          </div>
          <div class="form-group form-group-action">
            <button @click="addRetenue" class="btn btn-danger" :disabled="!newRetenueAmount">
              Ajouter
            </button>
          </div>
        </div>
      </div>

      <div v-if="records && records.length" class="table-card">
        <div class="table-head-bar">
          <h3>Détail des avances</h3>
          <span class="badge-count">{{ records.length }} entrée{{ records.length > 1 ? 's' : '' }}</span>
        </div>
        <div class="table-responsive">
          <table class="data-table mobile-card-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Mois déduit</th>
                <th>Montant</th>
                <th>Donné par</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, index) in records" :key="index">
                <td data-label="#"><span class="row-num">{{ index + 1 }}</span></td>
                <td data-label="Date">
                  <span class="date-chip">{{ convertdate(record.created_at) }}</span>
                </td>
                <td data-label="Mois déduit">
                  <span class="date-chip">{{ formatMonth(record.salary_month || selectedMonth) }}</span>
                </td>
                <td data-label="Montant">
                  <div v-if="editIndex === index">
                    <input type="number" step="0.01" v-model.number="record.avance" class="edit-input" />
                  </div>
                  <div v-else>
                    <span class="amount-chip">{{ record.avance.toFixed(2) }} DH</span>
                  </div>
                </td>
                <td data-label="Donné par"><span class="date-chip">{{ formatCreator(record) }}</span></td>
                <td data-label="Actions">
                  <div v-if="canManageAvances">
                    <div v-if="editIndex !== index">
                      <button @click="enableEdit(index)" class="btn-edit">Modifier</button>
                      <button @click="handleDelete(record.id)" class="btn-delete">Supprimer</button>
                    </div>
                    <div v-else>
                      <button @click="saveEdit(record)" class="btn-save">Sauvegarder</button>
                      <button @click="cancelEdit" class="btn-cancel">Annuler</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" style="text-align:right; font-weight:700; color: var(--gray-600);">Total avances</td>
                <td><span class="amount-chip amount-total">{{ totalAvance.toFixed(2) }} DH</span></td>
              </tr>
              <tr>
                <td colspan="4" style="text-align:right; font-weight:700; color: var(--gray-600);">Reste à régler
                </td>
                <td><span class="amount-chip amount-reste">{{ formatDirham(settlementRemainingAmount) }}
                    DH</span></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div v-if="avanceSuccess" class="alert alert-success">{{ avanceSuccess }}</div>

      <!-- Primes Table -->
      <div v-if="primes.length" class="table-card">
        <div class="table-head-bar">
          <h3>Détail des primes</h3>
          <span class="badge-count">{{ primes.length }} entrée{{ primes.length > 1 ? 's' : '' }}</span>
        </div>
        <div class="table-responsive">
          <table class="data-table mobile-card-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Montant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(prime, index) in primes" :key="index">
                <td data-label="#"><span class="row-num">{{ index + 1 }}</span></td>
                <td data-label="Date">
                  <span class="date-chip">{{ convertdate(prime.created_at) }}</span>
                </td>
                <td data-label="Montant">
                  <div v-if="editPrimeIndex === index">
                    <input type="number" step="0.01" v-model.number="prime.prime" class="edit-input" />
                  </div>
                  <div v-else>
                    <span class="amount-chip amount-prime">{{ (prime.prime || prime.montant || prime.amount || 0).toFixed(2) }} DH</span>
                  </div>
                </td>
                <td data-label="Actions">
                  <div v-if="canManageAvances">
                    <div v-if="editPrimeIndex !== index">
                      <button @click="enableEditPrime(index)" class="btn-edit">Modifier</button>
                      <button @click="handleDeletePrime(prime.id)" class="btn-delete">Supprimer</button>
                    </div>
                    <div v-else>
                      <button @click="saveEditPrime(prime)" class="btn-save">Sauvegarder</button>
                      <button @click="cancelEditPrime" class="btn-cancel">Annuler</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" style="text-align:right; font-weight:700; color: var(--gray-600);">Total primes</td>
                <td><span class="amount-chip amount-prime-total">{{ totalPrimesAmount.toFixed(2) }} DH</span></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div v-if="retenues.length" class="table-card">
        <div class="table-head-bar">
          <h3>Détail des retenues</h3>
          <span class="badge-count">{{ retenues.length }} entrée{{ retenues.length > 1 ? 's' : '' }}</span>
        </div>
        <div class="table-responsive">
          <table class="data-table mobile-card-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Motif</th>
                <th>Montant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(retenue, index) in retenues" :key="retenue.id">
                <td data-label="#"><span class="row-num">{{ index + 1 }}</span></td>
                <td data-label="Date"><span class="date-chip">{{ convertdate(retenue.created_at) }}</span></td>
                <td data-label="Motif">{{ retenue.reason || '-' }}</td>
                <td data-label="Montant"><span class="amount-chip amount-negative">{{ Number(retenue.amount || 0).toFixed(2) }} DH</span></td>
                <td data-label="Actions"><button @click="deleteRetenue(retenue.id)" class="btn-delete">Supprimer</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="salarySettlements.length" class="table-card">
        <div class="table-head-bar">
          <h3>Règlements du salaire {{ selectedMonth }}</h3>
          <span class="badge-count">{{ salarySettlements.length }} règlement{{ salarySettlements.length > 1 ? 's' : '' }}</span>
        </div>
        <div class="table-responsive">
          <table class="data-table mobile-card-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date réelle</th>
                <th>Montant</th>
                <th>Note</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(settlement, index) in salarySettlements" :key="settlement.id">
                <td data-label="#"><span class="row-num">{{ index + 1 }}</span></td>
                <td data-label="Date réelle"><span class="date-chip">{{ convertdate(settlement.paid_at) }}</span></td>
                <td data-label="Montant"><span class="amount-chip amount-reste">{{ formatDirham(settlement.amount) }} DH</span></td>
                <td data-label="Note">{{ settlement.note || '-' }}</td>
                <td data-label="Actions"><button @click="deleteSalarySettlement(settlement.id)" class="btn-delete">Supprimer</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- Salary Mass Table -->
    <div v-if="showMass" class="mass-section">
      <div class="section-title-wrap">
        <h2>Aperçu des salaires des employés</h2>
        <div class="filters-wrap">
          <div class="filter-group">
            <label class="form-label">Poste</label>
            <select v-model="selectedPost" @change="resetAndFetch" class="form-input" style="width: auto; min-width: 150px;">
              <option value="all">Tous les postes</option>
              <option v-for="post in posts" :key="post.Post_name" :value="post.Post_name">{{ post.Post_name }}</option>
            </select>
          </div>
          <div class="filter-group">
            <label class="form-label">Mois</label>
            <MonthPickerButton v-model="selectedMonth" @change="resetAndFetch" />
          </div>
        </div>
      </div>
      
      <div v-if="loadingMass" class="loading-indicator">
        <div class="spinner-ring"></div>
        <span>Chargement...</span>
      </div>

      <div v-if="!loadingMass && staffList.length" class="mass-mobile-list">
        <article v-for="staff in staffList" :key="staff.id" class="mass-mobile-card">
          <button class="mass-card-summary" type="button" @click="toggleMassCard(staff.id)">
            <div class="mass-staff-head">
              <div class="mass-staff-photo">
                <img v-if="staff.pic" :src="staffImage(staff)" :alt="staff.first_name" />
                <span v-else>{{ staffInitials(staff) }}</span>
              </div>
              <div class="mass-staff-title">
              <strong>{{ staff.first_name }} {{ staff.last_name }}</strong>
              <span>{{ staff.post_name }}</span>
              </div>
            </div>
            <div class="mass-card-status">
              <em :class="statusClass(staff.salary_status)">{{ statusLabel(staff.salary_status) }}</em>
              <svg :class="{ open: isMassCardOpen(staff.id) }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
          </button>
          <div class="mass-main-amount">
            <span>Reste à régler</span>
            <strong>{{ money(staff.reste_a_regler ?? staff.remaining_to_pay ?? Math.max(staff.reste || 0, 0)) }} DH</strong>
          </div>
          <div v-if="isMassCardOpen(staff.id)" class="mass-card-grid">
            <div>
              <span>Salaire mois</span>
              <strong>{{ money(staff.earned_salary ?? ((staff.days_worked || 0) * (staff.salary / 30))) }} DH</strong>
            </div>
            <div>
              <span>Jours</span>
              <strong>{{ staff.days_worked || 0 }} j</strong>
            </div>
            <div>
              <span>Avances</span>
              <strong>{{ money(staff.total_avance) }} DH</strong>
            </div>
            <div>
              <span>Primes</span>
              <strong>{{ money(staff.total_prime) }} DH</strong>
            </div>
            <div>
              <span>Retenues</span>
              <strong>{{ money(staff.total_retenue) }} DH</strong>
            </div>
            <div>
              <span>Absences</span>
              <strong>{{ staff.absences_count || 0 }}</strong>
            </div>
          </div>
          <button v-if="isMassCardOpen(staff.id)" class="mass-card-action" type="button" @click="viewStaffDetail(staff)">
            Gérer le salaire
          </button>
        </article>
      </div>

      <div v-if="!loadingMass && staffList.length" class="table-card mass-desktop-table">
        <div class="table-responsive" @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp" @mouseleave="handleMouseUp" :class="{ 'dragging': isDragging }">
          <table class="data-table mass-table">
            <thead>
              <tr>
                <th>Poste</th>
                <th>Nom</th>
                <th>Salaire mois</th>
                <th>Jours</th>
                <th>Jours RH payés</th>
                <th>Contrôle heures</th>
                <th>Total avances</th>
                <th>Primes</th>
                <th>Report</th>
                <th>Retenues</th>
                <th>Net mois</th>
                <th>Salaire/jour</th>
                <th>Absences</th>
                <th>Salaire du mois</th>
                <th>Reste</th>
                <th>Réglé</th>
                <th>À récupérer</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(group, postName) in groupedStaff" :key="postName">
                <tr v-for="(staff, index) in group" :key="staff.id" @click="!dragMoved && viewStaffDetail(staff)" style="cursor: pointer;" @mousedown="dragMoved = false">
                  <td v-if="index === 0" :rowspan="group.length" >
                    <span class="post-name-chip" >
                      {{ postName }}
                    </span>
                  </td>
                  <td>
                    <div class="staff-name-cell">
                      <div class="staff-table-photo">
                        <img v-if="staff.pic" :src="staffImage(staff)" :alt="staff.first_name" />
                        <span v-else>{{ staffInitials(staff) }}</span>
                      </div>
                      <span>{{ staff.first_name }} {{ staff.last_name }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="stat-chip chip-info">{{ staff.salary.toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-info">{{ staff.days_worked }} j</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-info">{{ staff.paid_days || 0 }} j</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-teal">{{ (staff.total_hours || 0).toFixed(2) }} h</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-purple">{{ staff.total_avance.toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-orange">{{ staff.total_prime.toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-red-soft">{{ (staff.previous_debt ?? staff.carry_over ?? 0).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-red-soft">{{ (staff.total_retenue || 0).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-blue">{{ (staff.month_net ?? staff.amount_due ?? 0).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-teal">{{ (staff.salary / 30).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-red"> {{ staff.absences_count || 0 }} </span>
                  </td>
                  <td>
                    <span class="stat-chip chip-success">{{ (staff.earned_salary ?? ((staff.days_worked || 0) * (staff.salary / 30))).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-green">{{ (staff.reste_a_regler ?? staff.remaining_to_pay ?? Math.max(staff.reste || 0, 0)).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-blue">{{ (staff.total_settled || 0).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip chip-red-soft">{{ (staff.advance_recovery_remaining ?? staff.advance_to_recover ?? 0).toFixed(2) }} DH</span>
                  </td>
                  <td>
                    <span class="stat-chip" :class="statusClass(staff.salary_status)">{{ statusLabel(staff.salary_status) }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2"></td>
                <td style="font-weight:700;">Salaire mois</td>
                <td style="font-weight:700;">Jours</td>
                <td style="font-weight:700;">Jours RH payés</td>
                <td style="font-weight:700;">Contrôle heures</td>
                <td style="font-weight:700;">Avances</td>
                <td style="font-weight:700;">Primes</td>
                <td style="font-weight:700;">Report</td>
                <td style="font-weight:700;">Retenues</td>
                <td style="font-weight:700;">Net mois</td>
                <td style="font-weight:700;">Salaire/jour</td>
                <td style="font-weight:700;">Absences</td>
                <td style="font-weight:700;">Salaire du mois</td>
                <td style="font-weight:700;">Reste</td>
                <td style="font-weight:700;">Réglé</td>
                <td style="font-weight:700;">À récupérer</td>
                <td style="font-weight:700;">Statut</td>
              </tr>
              <tr>
                <td colspan="2" style="font-weight:700;">Totale</td>
                <td style="font-weight:700;">{{ totalSalary.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalDaysWorked }}</td>
                <td style="font-weight:700;">{{ totalPaidDays }}</td>
                <td style="font-weight:700;">
                  <span>{{ totalHoursWorked.toFixed(2) }} h</span>
                </td>
                <td style="font-weight:700;">{{ totalAdvance.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalPrime.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalCarryOver.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalRetenue.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalDue.toFixed(2) }} DH</td>
                <td></td>
                <td style="font-weight:700;">{{ totalAbsences }}</td>
                <td style="font-weight:700;">{{ totalMonthSalary.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalReste.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalSettled.toFixed(2) }} DH</td>
                <td style="font-weight:700;">{{ totalAdvanceRecover.toFixed(2) }} DH</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <!-- Load More Button -->
      <div v-if="staffList.length > 0 && hasMore" class="load-more-wrap">
        <button @click="loadMore" :disabled="loadingMass" class="btn btn-secondary">
          <span v-if="loadingMass">Chargement...</span>
          <span v-else>Charger plus ({{ staffList.length }}/{{ totalCount }})</span>
        </button>
      </div>
      <div v-else-if="staffList.length > 0" class="pagination-info">
        Tous les employés affichés ({{ totalCount }})
      </div>

      <p v-else-if="!loadingMass" class="info-notice">Pas de données pour ce mois!</p>
    </div>
  </div>
</template>

<script>
import MonthPickerButton from './MonthPickerButton.vue';

export default {
  inject: ["privilage"],
  name: 'AppStaffAvance',
  components: { MonthPickerButton },
  props: {
    staffId: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      showMass: true,
      selectedMonthsinglstaff: null,
      selectedMonth: null,
      staffList: [],
      reportTitle: '',
      fetchError: null,
      searchError: null,
      selectedStaff: null,
      searchQuery: null,
      users: [],
      records: [],
      primes: [],
      retenues: [],
      salarySettlements: [],
      selectedStaffMassData: null,
      editedRecord: null,
      editIndex: null,
      editedPrime: null,
      editPrimeIndex: null,
      loading: false,
      error: null,
      // Pagination & filtering
      posts: [],
      selectedPost: 'all',
      currentPage: 1,
      limit: 50,
      totalCount: 0,
      hasMore: true,
      loadingMass: false,
      newAvanceAmount: null,
      newPrimeAmount: null,
      newSettlementAmount: null,
      newSettlementNote: '',
      newRetenueAmount: null,
      newRetenueReason: '',
      currentDateTime: '',
      currentDateTimeManual: false,
      currentDateTimeTimer: null,
      addingAvance: false,
      avanceSuccess: '',
      openActionPanel: '',
      openMassCards: {},
      // Drag scroll properties
      isDragging: false,
      dragStartX: 0,
      dragScrollLeft: 0,
      dragMoved: false
    }
  },
  computed: {
    canManageAvances() {
      return this.privilage && this.privilage() === 'admin';
    },
    groupedStaff() {
      const groups = {};
      this.staffList.forEach(staff => {
        if (!groups[staff.post_name]) {
          groups[staff.post_name] = [];
        }
        groups[staff.post_name].push(staff);
      });
      return groups;
    },
    totalAvance() {
      return this.records.reduce((sum, r) => sum + parseFloat(r.avance || 0), 0);
    },
    totalPrimesAmount() {
      return this.primes.reduce((sum, r) => sum + parseFloat(r.prime || r.montant || r.amount || 0), 0);
    },
    totalRetenuesAmount() {
      return this.retenues.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    },
    totalSettlementsAmount() {
      return this.salarySettlements.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    },
    carryOverAmount() {
      return parseFloat(this.selectedStaffMassData?.previous_debt ?? this.selectedStaffMassData?.carry_over ?? 0);
    },
    monthWorkedDays() {
      return Number(this.selectedStaffMassData?.days_worked ?? this.selectedStaff?.DaysWorked ?? 0);
    },
    monthEarnedSalary() {
      return Number(this.selectedStaffMassData?.earned_salary ?? (this.monthWorkedDays * (this.staffSalary / 30)));
    },
    monthNetAmount() {
      return Number(this.selectedStaffMassData?.month_net ?? (this.monthEarnedSalary + this.totalPrimesAmount - this.totalAvance - this.totalRetenuesAmount));
    },
    settlementRemainingAmount() {
      if (this.selectedStaffMassData) {
        return Number(this.selectedStaffMassData.reste_a_regler ?? this.selectedStaffMassData.remaining_to_pay ?? 0);
      }
      return this.calculateResteDH();
    },
    settlementRemainingRounded() {
      return this.roundMoney(this.settlementRemainingAmount);
    },
    settlementAmountRounded() {
      return this.roundMoney(this.newSettlementAmount || 0);
    },
    advanceToRecoverAmount() {
      return Number(this.selectedStaffMassData?.advance_to_recover || 0);
    },
    advanceRecoveredAmount() {
      return Number(this.selectedStaffMassData?.advance_recovered || 0);
    },
    advanceRecoveryRemainingAmount() {
      return Number(this.selectedStaffMassData?.advance_recovery_remaining || 0);
    },
    salaryStatusText() {
      return this.statusLabel(this.selectedStaffMassData?.salary_status);
    },
    salaryStatusClass() {
      return this.statusClass(this.selectedStaffMassData?.salary_status);
    },
    staffSalary() {
      if (this.selectedStaffMassData?.salary) {
        return this.selectedStaffMassData.salary;
      }
      // First, check if selectedStaff has salary property
      if (this.selectedStaff && this.selectedStaff.Salary) {
        return this.selectedStaff.Salary;
      }
      // Get salary from records (avances) or primes
      if (this.records && this.records.length > 0 && this.records[0].staff_salary) {
        return this.records[0].staff_salary;
      }
      if (this.primes && this.primes.length > 0 && this.primes[0].staff_salary) {
        return this.primes[0].staff_salary;
      }
      return 0;
    },
    totalSalary() {
      return this.staffList.reduce((sum, staff) => sum + staff.salary, 0);
    },
    totalDaysWorked() {
      return this.staffList.reduce((sum, staff) => sum + (staff.days_worked || 0), 0);
    },
    totalHoursWorked() {
      return this.staffList.reduce((sum, staff) => sum + (staff.total_hours || 0), 0);
    },
    totalPaidDays() {
      return this.staffList.reduce((sum, staff) => sum + (staff.paid_days || 0), 0);
    },
    totalAdvance() {
      return this.staffList.reduce((sum, staff) => sum + staff.total_avance, 0);
    },
    totalPrime() {
      return this.staffList.reduce((sum, staff) => sum + (staff.total_prime || 0), 0);
    },
    totalCarryOver() {
      return this.staffList.reduce((sum, staff) => sum + (staff.previous_debt ?? staff.carry_over ?? 0), 0);
    },
    totalRetenue() {
      return this.staffList.reduce((sum, staff) => sum + (staff.total_retenue || 0), 0);
    },
    totalDue() {
      return this.staffList.reduce((sum, staff) => sum + (staff.amount_due || 0), 0);
    },
    totalAbsences() {
      return this.staffList.reduce((sum, staff) => sum + staff.absences_count, 0);
    },
    totalReste() {
      return this.staffList.reduce((sum, staff) => sum + (staff.reste_a_regler ?? staff.remaining_to_pay ?? Math.max(staff.reste || 0, 0)), 0);
    },
    totalSettled() {
      return this.staffList.reduce((sum, staff) => sum + (staff.total_settled || 0), 0);
    },
    totalAdvanceRecover() {
      return this.staffList.reduce((sum, staff) => sum + (staff.advance_recovery_remaining ?? staff.advance_to_recover ?? 0), 0);
    },
    totalMonthSalary() {
      return this.staffList.reduce((sum, staff) => sum + (staff.earned_salary ?? ((staff.days_worked || 0) * (staff.salary / 30))), 0);
    }
  },
  watch: {
    staffId: {
      immediate: true,
      handler() {
        this.fetchAvances()
      }
    },
    searchQuery(newVal) {
      if (newVal == "") {
        this.selectedStaff = null
        this.fetchError = null
        this.showMass = true
      }
    },
  },
  methods: {
    noStoreFetch(url, options = {}) {
      const separator = url.includes('?') ? '&' : '?';
      return fetch(`${url}${separator}_=${Date.now()}`, {
        ...options,
        credentials: options.credentials || 'include',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          ...(options.headers || {}),
        },
      });
    },
    async searchUsers() {
      this.records = []
      this.avanceSuccess = ''
      if (this.searchQuery.trim() === '') {
        this.users = [];
        return;
      }

      try {
        const res = await this.noStoreFetch(`/api-pointage/search?query=${encodeURIComponent(this.searchQuery)}`);
        if (!res.ok) {
          const error = await res.json().catch(() => null);
          throw new Error(error?.error || "Pas d'employé trouvé");
        }
        const data = await res.json();

        if (data.length) {
          this.searchError = null
          this.users = data;
        } else {
          this.selectedStaff = null
          this.users = [];
          this.searchError = "Aucun employé trouvé"
        }
      } catch (err) {
        console.error(err);
        this.searchError = err.message;
        this.users = [];
      }
    },
    returnToList() {
      this.selectedStaff = null;
      this.selectedStaffMassData = null;
      this.records = [];
      this.primes = [];
      this.retenues = [];
      this.salarySettlements = [];
      this.users = [];
      this.searchQuery = '';
      this.fetchError = null;
      this.error = null;
      this.showMass = true;
    },
    async fetchAvances(staff) {
      if (!staff || !staff.Id) {
        this.selectedStaffMassData = null
        return
      }

      this.selectedStaff = {
        ...staff,
        DaysWorked: 0,
      }
      this.loading = true
      this.error = null
      this.records = []
      this.primes = []
      this.retenues = []
      this.salarySettlements = []
      this.selectedStaffMassData = null
      this.users = []
      // Reset form inputs
      this.newAvanceAmount = null;
      this.newPrimeAmount = null;
      this.newSettlementAmount = null;
      this.newSettlementNote = '';
      this.newRetenueAmount = null;
      this.newRetenueReason = '';
      this.currentDateTimeManual = false;
      this.refreshCurrentDateTime();
      this.openActionPanel = '';

      try {
        const requests = [
          this.noStoreFetch(`/api-pointage/getavances?staff_id=${staff.Id}&month=${this.selectedMonth}`),
          this.noStoreFetch(`/api-pointage/getprimes?staff_id=${staff.Id}&month=${this.selectedMonth}`),
          this.noStoreFetch(`/api-pointage/getretenues?staff_id=${staff.Id}&month=${this.selectedMonth}`),
          this.noStoreFetch(`/api-pointage/getsalarysettlements?staff_id=${staff.Id}&month=${this.selectedMonth}`),
          this.noStoreFetch(`/api-pointage/salarymass?month=${this.selectedMonth}&staff_id=${staff.Id}&page=1&limit=1`)
        ];
        const [avRes, secondRes, thirdRes, fourthRes, fifthRes] = await Promise.all(requests);
        
        let avData = null, primeData = null, retenueData = null, settlementData = null, massData = null;
        if (avRes.ok) avData = await avRes.json();
        if (secondRes?.ok) primeData = await secondRes.json();
        if (thirdRes?.ok) retenueData = await thirdRes.json();
        if (fourthRes?.ok) settlementData = await fourthRes.json();
        if (fifthRes?.ok) massData = await fifthRes.json();

        const staffMassData = Array.isArray(massData) && massData.length ? massData[0] : null;
        this.selectedStaffMassData = staffMassData;

        if (staffMassData) {
          this.selectedStaff = {
            ...staff,
            Salary: staffMassData.salary ?? staff.Salary ?? 0,
            DaysWorked: staffMassData.days_worked ?? staff.DaysWorked ?? 0,
            TotalHours: staffMassData.total_hours ?? staff.TotalHours ?? 0
          }
          const suggestedSettlement = this.roundMoney(staffMassData.reste_a_regler ?? staffMassData.remaining_to_pay ?? 0);
          this.newSettlementAmount = suggestedSettlement > 0 ? suggestedSettlement : null;
        }

        const hasDetails = [avData, primeData, retenueData, settlementData].some(items => items && items.length > 0);
        if (hasDetails || staffMassData) {
          this.fetchError = null
          this.records = avData || []
          this.primes = primeData || []
          this.retenues = retenueData || []
          this.salarySettlements = settlementData || []
          this.showMass = false
        } else {
          this.showMass = false
          this.fetchError = "Aucune donnée salaire pour ce mois!"
        }
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    enableEdit(index) {
      this.editIndex = index
      this.editedRecord = JSON.parse(JSON.stringify(this.records[index]))
    },
    cancelEdit() {
      if (this.editIndex !== null) {
        this.records.splice(this.editIndex, 1, this.editedRecord)
      }
      this.editIndex = null
      this.editedRecord = null
    },
    async saveEdit(record) {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) {
          throw new Error("Aucun employé sélectionné");
        }
        
        const res = await fetch(`/api-pointage/updateavance`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: record.id,
            staff_id: parseInt(staffId),
            avance: parseFloat(record.avance)
          })
        })
        if (!res.ok) throw new Error("Échec de la mise à jour")
        
        this.editIndex = null
        this.editedRecord = null
        alert("Avance mise à jour!")
        
        // Rafraîchir les données
        if (this.selectedStaff) {
          await this.fetchAvances(this.selectedStaff);
        }
      } catch (err) {
        alert(err.message)
      }
    },
    async handleDelete(id) {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) {
          throw new Error("Aucun employé sélectionné");
        }
        
        const res = await fetch(`/api-pointage/deleteavance`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ staff_id: parseInt(staffId), id })
        })
        if (!res.ok) throw new Error("Échec de suppression")
        alert("Avance supprimée")
        
        // Rafraîchir les données
        if (this.selectedStaff) {
          await this.fetchAvances(this.selectedStaff);
        }
      } catch (err) {
        alert("Erreur: " + err.message)
      }
    },
    async addPrime() {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) {
          throw new Error("Aucun employé sélectionné");
        }
        if (!this.newPrimeAmount) {
          throw new Error("Veuillez saisir le montant de la prime");
        }

        const res = await fetch(`/api-pointage/addprime`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            staff_id: parseInt(staffId),
            prime: parseFloat(this.newPrimeAmount),
            primeDate: this.currentDateTime,
            salary_month: this.selectedMonth
          })
        });
        if (!res.ok) {
          const error = await res.json().catch(() => null);
          throw new Error(error?.error || "Échec de l'ajout de la prime");
        }

        this.avanceSuccess = "Prime ajoutée avec succès.";
        this.newPrimeAmount = null;
        if (this.selectedStaff) {
          await this.fetchAvances(this.selectedStaff);
        }
      } catch (err) {
        alert("Erreur: " + err.message);
      }
    },
    enableEditPrime(index) {
      this.editPrimeIndex = index
      this.editedPrime = JSON.parse(JSON.stringify(this.primes[index]))
    },
    cancelEditPrime() {
      if (this.editPrimeIndex !== null) {
        this.primes.splice(this.editPrimeIndex, 1, this.editedPrime)
      }
      this.editPrimeIndex = null
      this.editedPrime = null
    },
    async saveEditPrime(prime) {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) {
          throw new Error("Aucun employé sélectionné");
        }
        
        const res = await fetch(`/api-pointage/updateprime`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: prime.id,
            staff_id: parseInt(staffId),
            prime: parseFloat(prime.prime)
          })
        })
        if (!res.ok) throw new Error("Échec de la mise à jour")
        
        this.editPrimeIndex = null
        this.editedPrime = null
        alert("Prime mise à jour!")
        
        // Rafraîchir les données
        if (this.selectedStaff) {
          await this.fetchAvances(this.selectedStaff);
        }
      } catch (err) {
        alert(err.message)
      }
    },
    async handleDeletePrime(id) {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) {
          throw new Error("Aucun employé sélectionné");
        }
        
        const res = await fetch(`/api-pointage/deleteprime`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ staff_id: parseInt(staffId), id })
        })
        if (!res.ok) throw new Error("Échec de suppression")
        alert("Prime supprimée")
        
        // Rafraîchir les données
        if (this.selectedStaff) {
          await this.fetchAvances(this.selectedStaff);
        }
      } catch (err) {
        alert("Erreur: " + err.message)
      }
    },
    async addSalarySettlement() {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) throw new Error("Aucun employé sélectionné");
        if (!this.selectedMonth || !this.newSettlementAmount) {
          throw new Error("Mois et montant requis");
        }
        if (this.settlementRemainingRounded <= 0) {
          throw new Error("Aucun reste à régler pour ce mois");
        }
        if (this.settlementAmountRounded > this.settlementRemainingRounded) {
          throw new Error(`Le règlement ne peut pas dépasser ${this.formatDirham(this.settlementRemainingRounded)} DH`);
        }

        const res = await fetch(`/api-pointage/addsalarysettlement`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            staff_id: parseInt(staffId),
            salary_month: this.selectedMonth,
            paid_at: this.currentDateTime,
            amount: this.settlementAmountRounded,
            note: this.newSettlementNote || ''
          })
        });
        if (!res.ok) {
          const error = await res.json().catch(() => null);
          throw new Error(error?.error || "Échec du règlement");
        }

        this.avanceSuccess = "Règlement salaire enregistré.";
        await this.fetchAvances(this.selectedStaff);
      } catch (err) {
        alert("Erreur: " + err.message);
      }
    },
    async deleteSalarySettlement(id) {
      try {
        const res = await fetch(`/api-pointage/deletesalarysettlement`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id })
        });
        if (!res.ok) throw new Error("Échec de suppression");
        await this.fetchAvances(this.selectedStaff);
      } catch (err) {
        alert("Erreur: " + err.message);
      }
    },
    async addRetenue() {
      try {
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) throw new Error("Aucun employé sélectionné");
        if (!this.newRetenueAmount) {
          throw new Error("Montant requis");
        }

        const res = await fetch(`/api-pointage/addretenue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            staff_id: parseInt(staffId),
            amount: parseFloat(this.newRetenueAmount),
            reason: this.newRetenueReason || '',
            date: this.currentDateTime,
            salary_month: this.selectedMonth
          })
        });
        if (!res.ok) {
          const error = await res.json().catch(() => null);
          throw new Error(error?.error || "Échec de la retenue");
        }

        this.avanceSuccess = "Retenue enregistrée.";
        await this.fetchAvances(this.selectedStaff);
      } catch (err) {
        alert("Erreur: " + err.message);
      }
    },
    async deleteRetenue(id) {
      try {
        const res = await fetch(`/api-pointage/deleteretenue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id })
        });
        if (!res.ok) throw new Error("Échec de suppression");
        await this.fetchAvances(this.selectedStaff);
      } catch (err) {
        alert("Erreur: " + err.message);
      }
    },
    resetAndFetch() {
      this.currentPage = 1;
      this.staffList = [];
      this.hasMore = true;
      this.openMassCards = {};
      this.fetchStaffData();
    },
    async fetchStaffData(append = false) {
      try {
        this.loadingMass = true;
                
        // Build URL with pagination and post filter
        let url = `/api-pointage/salarymass?month=${this.selectedMonth}&page=${this.currentPage}&limit=${this.limit}`;
        if (this.selectedPost && this.selectedPost !== 'all') {
          url += `&post=${encodeURIComponent(this.selectedPost)}`;
        }
        
        const staffResponse = await this.noStoreFetch(url);
        if (!staffResponse.ok) throw new Error('Network error');
        
        // Read pagination headers
        this.totalCount = parseInt(staffResponse.headers.get('X-Total-Count') || '0');
        
        let staffData = await staffResponse.json();
        
        if (staffData != null && staffData.length > 0) {
          // Data already has correct totals from backend
          if (append) {
            this.staffList.push(...staffData);
          } else {
            this.staffList = staffData;
          }
          
          // Check if there are more pages
          this.hasMore = (this.currentPage * this.limit) < this.totalCount;
        } else if (!append) {
          this.staffList = [];
          this.hasMore = false;
        }
      } catch (error) {
        console.error('Error fetching staff ', error);
        if (!append) this.staffList = [];
      } finally {
        this.loadingMass = false;
      }
    },
    async loadMore() {
      if (this.loadingMass || !this.hasMore) return;
      this.currentPage += 1;
      await this.fetchStaffData(true);
    },
    viewStaffDetail(staff) {
      // Convert staff data format to match fetchAvances expectations
      const staffData = {
        Id: staff.id,
        First_name: staff.first_name,
        Last_name: staff.last_name,
        Post_name: staff.post_name,
        Pic: staff.pic || null,
        Salary: staff.salary || 0,
        DaysWorked: staff.days_worked || 0,
        TotalHours: staff.total_hours || 0
      };
      this.fetchAvances(staffData);
    },
    async addNewAvance() {
      try {
        if (!this.newAvanceAmount) {
          alert('Veuillez saisir le montant');
          return;
        }
        
        const staffId = this.selectedStaff?.Id || this.staffId;
        if (!staffId) {
          throw new Error('Aucun employé sélectionné');
        }
        this.addingAvance = true;
        
        const res = await fetch(`/api-pointage/addavance`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            staff_id: parseInt(staffId),
            avance: parseFloat(this.newAvanceAmount),
            avanceDate: this.currentDateTime,
            salary_month: this.selectedMonth
          })
        });
        
        if (!res.ok) {
          const error = await res.json().catch(() => null);
          throw new Error(error?.error || 'Échec de l\'ajout de l\'avance');
        }
        
        this.avanceSuccess = 'Avance ajoutée avec succès.';
        this.newAvanceAmount = null;
        setTimeout(() => {
          this.avanceSuccess = '';
        }, 3000);
        
        // Rafraîchir les données
        if (this.selectedStaff) {
          await this.fetchAvances(this.selectedStaff);
        }
      } catch (err) {
        alert('Erreur: ' + err.message);
      } finally {
        this.addingAvance = false;
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },
    convertdate(input) {
      const [date, time] = input.split(" ");
      const [year, month, day] = date.split("-");
      const [hours, minutes] = time.split(":");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },
    convertShortDate(input) {
      if (!input) return '';
      const [year, month, day] = input.split('-');
      return `${day}/${month}/${year}`;
    },
    formatMonth(input) {
      if (!input) return '-';
      const [year, month] = String(input).split('-');
      if (!year || !month) return input;
      const date = new Date(Number(year), Number(month) - 1, 1);
      return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    },
    formatCreator(record) {
      if (!record) return '-';
      return record.created_by_name || 'Admin';
    },
    isActionOpen(panel) {
      return this.openActionPanel === panel;
    },
    toggleActionPanel(panel) {
      this.openActionPanel = this.openActionPanel === panel ? '' : panel;
    },
    isMassCardOpen(staffID) {
      return Boolean(this.openMassCards[staffID]);
    },
    toggleMassCard(staffID) {
      this.openMassCards = {
        ...this.openMassCards,
        [staffID]: !this.openMassCards[staffID]
      };
    },
    money(value) {
      return Number(value || 0).toLocaleString('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    statusLabel(status) {
      const labels = {
        non_regle: 'Non réglé',
        partiellement_regle: 'Partiel',
        regle: 'Réglé',
        rien_a_regler: 'Rien à régler',
        a_recuperer: 'À récupérer',
        partiellement_recupere: 'Récupération partielle',
        recupere: 'Récupéré'
      };
      return labels[status] || 'Non réglé';
    },
    statusClass(status) {
      const classes = {
        non_regle: 'chip-red',
        partiellement_regle: 'chip-orange',
        regle: 'chip-green',
        rien_a_regler: 'chip-info',
        a_recuperer: 'chip-red-soft',
        partiellement_recupere: 'chip-orange',
        recupere: 'chip-green'
      };
      return classes[status] || 'chip-info';
    },
    staffInitials(staff) {
      return `${staff?.first_name?.[0] || staff?.First_name?.[0] || ''}${staff?.last_name?.[0] || staff?.Last_name?.[0] || ''}` || '?';
    },
    staffImage(staff) {
      const pic = staff?.pic || staff?.Pic || '';
      if (!pic) return '/pointage/profile/profile.jpg';
      return pic.startsWith('/api-pointage/uploads/') || pic.startsWith('/pointage/')
        ? pic
        : `/api-pointage/uploads/${pic}`;
    },
    nowForInput() {
      const now = new Date();
      const offsetMs = now.getTimezoneOffset() * 60000;
      return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
    },
    roundMoney(value) {
      return Math.round(Number(value || 0) + Number.EPSILON);
    },
    formatDirham(value) {
      return this.roundMoney(value).toLocaleString('fr-FR');
    },
    refreshCurrentDateTime() {
      if (this.currentDateTimeManual) return;
      this.currentDateTime = this.nowForInput();
    },
    markDateTimeManual() {
      this.currentDateTimeManual = true;
    },
    calculateResteDH() {
      if (this.selectedStaffMassData && Number.isFinite(this.selectedStaffMassData.reste)) {
        return this.selectedStaffMassData.remaining_to_pay ?? Math.max(this.selectedStaffMassData.reste, 0);
      }
      return Math.max(this.calculateRawBalanceDH(), 0);
    },
    calculateRawBalanceDH() {
      if (this.selectedStaffMassData && Number.isFinite(this.selectedStaffMassData.net_balance)) {
        return this.selectedStaffMassData.net_balance;
      }

      const salary = this.staffSalary;
      if (salary === 0) return 0;
      
      const days = this.selectedStaffMassData?.days_worked ?? this.selectedStaff?.DaysWorked ?? 0;

      return ((salary / 30) * days)
        - this.totalAvance
        + this.totalPrimesAmount
        - this.totalRetenuesAmount
        - this.carryOverAmount
        - this.totalSettlementsAmount;
    },
    async fetchPosts() {
      try {
        const response = await this.noStoreFetch(`/api-pointage/fetchposts`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        this.posts = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching posts:", error);
        this.posts = [];
      }
    },
    handleMouseDown(event) {
      // Only allow left mouse button
      if (event.button !== 0) return;
      
      const tableResponsive = event.currentTarget;
      this.isDragging = true;
      this.dragStartX = event.pageX - tableResponsive.offsetLeft;
      this.dragScrollLeft = tableResponsive.scrollLeft;
      event.preventDefault();
    },
    handleMouseMove(event) {
      if (!this.isDragging) return;
      
      const tableResponsive = event.currentTarget;
      const x = event.pageX - tableResponsive.offsetLeft;
      const walk = (x - this.dragStartX) * 1; // Multiply by 1 for scroll speed
      
      // Si la souris a bougé plus de 5px, c'est un drag
      if (Math.abs(walk) > 5) {
        this.dragMoved = true;
      }
      
      tableResponsive.scrollLeft = this.dragScrollLeft - walk;
    },
    handleMouseUp() {
      this.isDragging = false;
    }
  },
  mounted() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const formatted = `${year}-${month}`;
    this.selectedMonth = formatted
    this.refreshCurrentDateTime()
    this.currentDateTimeTimer = setInterval(this.refreshCurrentDateTime, 30000)
    this.fetchPosts()
    this.fetchStaffData()
  },
  beforeUnmount() {
    if (this.currentDateTimeTimer) {
      clearInterval(this.currentDateTimeTimer)
    }
  }
}
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: slideUp 0.25s ease;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-title-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  text-align: left;
}

.page-header h1 {
  font-size: 24px;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--gray-500);
}

.search-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: var(--shadow-sm);
}

.search-wrap {
  position: relative;
}

.search-icon-svg {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--gray-400);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 38px !important;
  background: var(--gray-50) !important;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: 14px;
  transition: all var(--transition);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-glow);
}

.search-results {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition);
}

.search-result-item:hover {
  background: var(--primary-glow);
}

.result-avatar {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid var(--border);
}

.result-name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--gray-800);
}

.result-post {
  font-size: 12px;
  color: var(--gray-500);
}

.result-arrow {
  width: 16px;
  height: 16px;
  color: var(--gray-400);
  margin-left: auto;
}

.search-error {
  padding: 10px 14px;
  font-size: 13px;
  color: var(--warning);
}

.info-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: var(--primary-glow);
  border: 1px solid rgba(26, 86, 219, 0.15);
  border-radius: var(--radius-sm);
  color: var(--primary);
  font-size: 13px;
}

.info-notice svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.staff-banner {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: var(--shadow-sm);
}

.back-list-btn {
  height: 42px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-list-btn:hover {
  border-color: var(--primary);
  color: var(--primary);
  background: var(--primary-glow);
}

.back-list-btn svg {
  width: 17px;
  height: 17px;
}

.banner-avatar {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid var(--border);
}

.banner-info {
  flex: 1;
}

.banner-info h2 {
  font-size: 18px;
  margin-bottom: 5px;
}

.month-select-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.days-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.days-badge {
  background-color: var(--primary-glow);
  color: var(--primary);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.form-label {
  font-size: 11px;
  color: var(--gray-500);
  font-weight: 600;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--white);
  font-size: 13px;
  transition: all var(--transition);
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-glow);
}

.staff-post-badge {
  display: inline-flex;
  padding: 3px 10px;
  background: var(--primary-glow);
  color: var(--primary);
  border-radius: var(--radius-full);
  font-size: 11.5px;
  font-weight: 600;
}

.table-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.table-responsive {
  overflow-x: auto;
  cursor: grab;
  user-select: none;
}

.table-responsive.dragging {
  cursor: grabbing;
}

.table-head-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border);
}

.table-head-bar h3 {
  font-size: 15px;
}

.badge-count {
  background: var(--gray-100);
  color: var(--gray-600);
  padding: 3px 10px;
  border-radius: var(--radius-full);
  font-size: 12px;
  font-weight: 700;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.mass-table {
  min-width: 1200px;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-700);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--gray-50);
  border-bottom: 1px solid var(--border);
}

.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: var(--gray-800);
  border-bottom: 1px solid var(--border);
}

.data-table tbody tr:hover {
  background: var(--gray-50);
}

.data-table tfoot td {
  padding: 12px 16px;
  font-size: 13px;
  border-top: 2px solid var(--border);
}

.row-num {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  background: var(--gray-100);
  border-radius: 50%;
  font-size: 11px;
  font-weight: 700;
  color: var(--gray-500);
}

.date-chip {
  display: inline-flex;
  padding: 3px 8px;
  background: var(--gray-100);
  border-radius: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--gray-700);
}

.amount-chip {
  display: inline-flex;
  padding: 4px 12px;
  background: var(--warning-bg);
  color: var(--warning);
  border-radius: 5px;
  font-size: 13px;
  font-weight: 700;
}

.amount-total {
  background: var(--danger-bg);
  color: var(--danger);
}

.amount-reste {
  background: var(--success-bg);
  color: var(--success);
}

.amount-negative {
  background: rgba(239,68,68,0.14);
  color: #dc2626;
}

.amount-prime {
  background: rgba(249,115,22,0.1);
  color: #f97316;
}

.amount-prime-total {
  background: rgba(249,115,22,0.1);
  color: #f97316;
}

.salary-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.summary-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: var(--shadow-sm);
}

.month-status-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: var(--shadow-sm);
}

.month-status-card div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.month-status-card strong {
  font-size: 15px;
  color: var(--gray-900);
}

.summary-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-icon svg {
  width: 18px;
  height: 18px;
}

.summary-blue .summary-icon {
  background: var(--primary-glow);
  color: var(--primary);
}

.summary-teal .summary-icon {
  background: rgba(20,184,166,0.12);
  color: #14b8a6;
}

.summary-amber .summary-icon {
  background: var(--warning-bg);
  color: var(--warning);
}

.summary-orange .summary-icon {
  background: rgba(249,115,22,0.1);
  color: #f97316;
}

.summary-green .summary-icon {
  background: var(--success-bg);
  color: var(--success);
}

.summary-red .summary-icon {
  background: rgba(239,68,68,0.12);
  color: #dc2626;
}

.summary-value {
  font-family: 'Arial', sans-serif;
  font-size: 20px;
  font-weight: 900;
  color: var(--gray-900);
}

.value-negative {
  color: #dc2626;
}

.summary-label {
  font-size: 12px;
  color: var(--gray-500);
  margin-top: 2px;
}

.salary-flow-panel {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0 0 12px;
}

.flow-section {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.flow-header {
  min-height: 46px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  background: #f8fafc;
}

.flow-header span,
.flow-item span {
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 800;
}

.flow-header strong {
  color: var(--gray-900);
  font-size: 14px;
}

.flow-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.flow-item {
  min-height: 68px;
  padding: 12px 14px;
  border-right: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.flow-item:nth-child(2n) {
  border-right: 0;
}

.flow-item:nth-last-child(-n + 2) {
  border-bottom: 0;
}

.flow-item strong {
  color: var(--gray-900);
  font-size: 16px;
}

.previous-flow .flow-header {
  background: #fff7ed;
}

.final-flow strong {
  color: var(--success);
}

.mass-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.section-title-wrap h2 {
  font-size: 20px;
}

.post-name-chip {
  display: inline-flex;
  padding: 5px 12px;
  color: #000;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

.staff-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 180px;
  font-size: 13.5px;
  font-weight: 800;
  color: var(--gray-800);
}

.staff-table-photo {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--primary-glow);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.staff-table-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.stat-chip {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
}

.hours-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.hours-stack small {
  color: var(--gray-500);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.chip-blue {
  background: var(--primary-glow);
  color: var(--primary);
}

.chip-info {
  background: rgba(14,165,233,0.1);
  color: #0ea5e9;
}

.chip-purple {
  background: rgba(124, 58, 237, 0.1);
  color: #7c3aed;
}

.chip-orange {
  background: rgba(249,115,22,0.1);
  color: #f97316;
}

.chip-teal {
  background: rgba(20,184,166,0.1);
  color: #14b8a6;
}

.chip-green {
  background: var(--success-bg);
  color: var(--success);
}

.chip-success {
  background: var(--success-bg);
  color: var(--success);
}

.chip-red-soft {
  background: rgba(248,113,113,0.12);
  color: #b91c1c;
}

.chip-red {
  background: rgba(239,68,68,0.14);
  color: #dc2626;
}

.btn-edit,
.btn-delete,
.btn-save,
.btn-cancel {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
  cursor: pointer;
  border: none;
}

.btn-edit {
  background: var(--primary);
  color: white;
}

.btn-delete {
  background: var(--danger);
  color: white;
}

.btn-save {
  background: var(--success);
  color: white;
}

.btn-cancel {
  background: var(--gray-400);
  color: white;
}

.edit-input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  font-size: 13px;
}

.avance-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

th {
  font-weight: bold !important;
  color: black !important;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    padding-left: 0;
    gap: 12px;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .search-section {
    position: sticky;
    top: calc(var(--header-height) + 8px);
    z-index: 20;
  }

  .search-card,
  .form-card,
  .table-card,
  .staff-banner {
    border-radius: 8px;
  }

  .search-input {
    min-height: 50px;
  }

  .search-result-item {
    min-height: 64px;
  }

  .banner-avatar {
    width: 58px;
    height: 58px;
  }

  .salary-summary {
    grid-template-columns: 1fr;
  }

  .salary-flow-panel,
  .flow-grid {
    grid-template-columns: 1fr;
  }

  .flow-item,
  .flow-item:nth-child(2n),
  .flow-item:nth-last-child(-n + 2) {
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }

  .flow-item:last-child {
    border-bottom: 0;
  }

  .staff-banner {
    flex-direction: column;
    align-items: flex-start;
  }

  .back-list-btn {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }

  .month-select-wrap {
    align-items: flex-start;
  }

  .section-title-wrap {
    flex-direction: column;
    align-items: flex-start;
  }

  .filters-wrap {
    flex-direction: column;
    width: 100%;
  }

  .form-group-row {
    flex-direction: column;
    align-items: stretch;
  }

  .form-group,
  .form-group-action,
  .form-group-action .btn {
    width: 100%;
  }

  .add-avance-form {
    background: var(--white);
    border-width: 1px;
  }

  .limit-grid {
    grid-template-columns: 1fr;
  }

  .btn-edit,
  .btn-delete,
  .btn-save,
  .btn-cancel {
    min-height: 40px;
    padding: 8px 10px;
    margin: 3px 0;
    width: 100%;
  }
}

/* Filters */
.filters-wrap {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Loading indicator */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: var(--gray-500);
  font-size: 14px;
}

.spinner-ring {
  width: 24px;
  height: 24px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Load more */
.load-more-wrap {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.pagination-info {
  text-align: center;
  padding: 16px;
  color: var(--gray-500);
  font-size: 13px;
}

.mass-mobile-list {
  display: none;
}

.mass-mobile-card {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--white);
  box-shadow: var(--shadow-sm);
  padding: 12px;
}

.mass-card-summary {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.mass-staff-head {
  display: grid;
  grid-template-columns: 66px minmax(0, 1fr);
  align-items: center;
  gap: 11px;
  min-width: 0;
}

.mass-staff-photo {
  width: 66px;
  height: 66px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--primary-glow);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  font-size: 22px;
  font-weight: 900;
  text-transform: uppercase;
}

.mass-staff-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mass-staff-title {
  min-width: 0;
}

.mass-card-status {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 10px;
  flex: 0 0 auto;
}

.mass-card-status svg {
  width: 18px;
  height: 18px;
  color: var(--gray-500);
  margin-top: 3px;
  transition: transform 0.18s ease;
}

.mass-card-status svg.open {
  transform: rotate(180deg);
}

.mass-card-summary strong,
.mass-card-summary span {
  display: block;
}

.mass-card-summary strong {
  color: var(--gray-900);
  font-size: 15px;
}

.mass-card-summary span {
  margin-top: 3px;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 700;
}

.mass-card-status em {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 5px 9px;
  font-size: 11px;
  font-style: normal;
  font-weight: 900;
}

.mass-main-amount {
  margin-top: 12px;
  border-radius: 8px;
  background: #f0fdf4;
  padding: 10px;
}

.mass-main-amount span,
.mass-card-grid span {
  display: block;
  color: var(--gray-500);
  font-size: 11px;
  font-weight: 800;
}

.mass-main-amount strong {
  display: block;
  margin-top: 4px;
  color: #059669;
  font-size: 20px;
  font-weight: 900;
}

.mass-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.mass-card-grid div {
  min-height: 58px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px;
}

.mass-card-grid strong {
  display: block;
  margin-top: 5px;
  color: var(--gray-900);
  font-size: 14px;
}

.mass-card-action {
  width: 100%;
  min-height: 42px;
  margin-top: 10px;
  border: 1px solid var(--primary);
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  font-weight: 900;
  cursor: pointer;
}

/* Form Card Styles */
.form-card {
  background: var(--white);
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: var(--shadow-sm);
}

.add-avance-form {
  background: var(--white);
  border: 1px solid var(--border);
}

.form-header {
  margin-bottom: 15px;
}

.form-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.action-accordion {
  padding: 0;
  overflow: hidden;
}

.action-accordion.open {
  padding-bottom: 14px;
}

.accordion-header {
  width: 100%;
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 0;
  border-bottom: 1px solid transparent;
  background: var(--white);
  padding: 0 14px;
  cursor: pointer;
  text-align: left;
}

.action-accordion.open .accordion-header {
  border-bottom-color: var(--border);
  margin-bottom: 14px;
}

.accordion-header span {
  flex: 0 0 auto;
  border-radius: 999px;
  background: var(--primary-glow);
  color: var(--primary);
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 900;
}

.action-accordion .form-group-row {
  padding: 0 14px;
}

.form-group-row {
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.form-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 150px;
}

.form-group-action {
  margin-top: 5px;
}

.form-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 6px;
  display: block;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  background-color: var(--input-bg, #fff);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-glow);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-success {
  background-color: #10b981;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #059669;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn svg {
  width: 16px;
  height: 16px;
}

.limit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.limit-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 12px;
  background: var(--gray-50);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.limit-box span,
.limit-box small {
  color: var(--gray-500);
  font-size: 12px;
}

.limit-box strong {
  color: var(--gray-900);
  font-size: 17px;
}

@media (max-width: 640px) {
  .page-container {
    gap: 10px;
  }

  .page-header {
    gap: 4px;
    margin-top: -6px;
  }

  .page-header h1 {
    font-size: 21px;
    line-height: 1.15;
    margin: 0;
  }

  .page-subtitle {
    font-size: 12px;
    line-height: 1.25;
  }

  .search-section {
    position: static;
  }

  .search-card {
    padding: 8px;
  }

  .search-input {
    min-height: 44px;
  }

  .mass-section {
    gap: 8px;
  }

  .section-title-wrap {
    gap: 8px;
  }

  .section-title-wrap h2 {
    font-size: 18px;
    line-height: 1.2;
  }

  .filters-wrap {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
    width: 100%;
  }

  .filter-group {
    min-width: 0;
    gap: 4px;
  }

  .filter-group .form-label {
    font-size: 10px;
    margin-bottom: 0;
  }

  .filter-group .form-input {
    width: 100% !important;
    min-width: 0 !important;
    min-height: 42px;
    font-size: 12px;
    padding: 0 10px;
  }

  .filter-group :deep(.month-picker-button) {
    width: 100%;
    min-width: 0;
  }

  .filter-group :deep(.month-trigger) {
    min-height: 42px;
    padding: 0 10px;
    font-size: 12px;
  }

  .mass-mobile-card {
    padding: 12px;
  }

  .mass-card-summary strong {
    font-size: 15px;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .mass-staff-head {
    grid-template-columns: 72px minmax(0, 1fr);
  }

  .mass-staff-photo {
    width: 72px;
    height: 72px;
  }

  .mass-main-amount {
    min-height: 46px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
    padding: 8px 10px;
  }

  .mass-main-amount strong {
    margin-top: 0;
    font-size: 18px;
  }

  .mass-card-grid {
    gap: 6px;
    margin-top: 8px;
  }

  .mass-card-grid div {
    min-height: 52px;
    padding: 7px;
  }

  .mass-card-action {
    min-height: 40px;
    margin-top: 8px;
  }

  .mass-desktop-table {
    display: none;
  }

  .mass-mobile-list {
    display: grid;
    gap: 10px;
  }

  .summary-card {
    min-height: 76px;
  }

  .summary-value {
    font-size: 18px;
  }

  .staff-banner {
    display: grid;
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .banner-info h2 {
    font-size: 18px;
  }
}
</style>
